import { type FC } from "react";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import { DotStepIndicator } from "@root/shared/components/DotStepIndicator";
import { Button } from "@root/shared/components/Button";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { Icon } from "@root/shared/components/Icon";
import { EMPLOYMENT } from "@root/constants/employments";
import { Information } from "@userFrontend/features/clientReview/components/Information";
import { colors } from "@root/shared/styles/sprinkles.css";
import { ClientSelectSection } from "@userFrontend/features/report/components/ClientSelectSection";
import { RestoreClientReviewInputsModal } from "./uiParts/RestoreClientReviewInputsModal";
import { SelectedClientInfo } from "./uiParts/SelectedClientInfo";
import { Employment } from "./uiParts/Employment";
import { TreatmentAndCulture } from "./uiParts/TreatmentAndCulture";
import { WorkplaceReviewFeature } from "./uiParts/WorkplaceReviewFeature";
import { SalaryData } from "./uiParts/SalaryData";
import { WorkplaceReviewThought } from "./uiParts/WorkplaceReviewThought";
import { DecidingFactors } from "./uiParts/DecidingFactors";
import { Satisfaction } from "./uiParts/Satisfaction";
import { Confirm } from "./uiParts/Confirm";
import * as styles from "./styles.css";

// types
import { type PresentationProps } from "./type";

export const Presentation: FC<PresentationProps> = ({
  currentStep,
  questions,
  client,
  clientShop,
  businessTypeValue,
  employmentStatusValue,
  continuedServiceYearsValue,
  occupationValue,
  employmentValue,
  monthlyIncomeValue,
  minimumGuaranteedIncomeValue,
  grossMonthlyIncomeValue,
  hourlyWageValue,
  noGuaranteedIncomeFlag,
  noBonusFlag,
  hiddenFlag,
  radioValues,
  checkboxValues,
  textAreaValue,
  bonusInputsProps,
  errorTexts,
  handleShowModal,
  handleBusinessTypeSelect,
  handleRadioChange,
  handleOccupationSelect,
  handleEmploymentSelect,
  handleMonthlyIncomeChange,
  handleMinimumGuaranteedIncomeChange,
  handleGrossMonthlyIncomeChange,
  handleHourlyWageChange,
  handleMinimumGuaranteedIncomeCheckboxChange,
  handleBonusCheckboxChange,
  handleHiddenFlagCheckboxChange,
  handleCheckboxChange,
  handleTextAreaChange,
  handleEmploymentStatusChange,
  handleContinuedServiceYearSelect,
  handleSatisfactionRateChange,
  handleClickPrevStepButton,
  handleClickNextStepButton,
  handleClickSubmitButton,
  isShowBackupModal,
  restoreBackupPath,
  deleteBackupPath,
  termsPath,
  guidelinePath,
  handleRestore,
  inputContainerRefs,
  isShowSearchModal,
  handleCloseModal,
  clientNameValue,
  shopNameValue,
  handleClientSearch,
  handleChangeClientNameValue,
  handleChangeShopNameValue,
  searchResults,
  handleClientSelect,
  isShowClientsDetail,
  handleShowTextButton,
  tooManyResultErrorText,
  noResultError,
  displaySearchText,
  isSearching,
  handleReturnToMyPageTop,
  userContactPath,
  salaryArray,
  bonusArray,
  checkedJobEntryId,
  jobEntryButtons,
  satisfactionRateValue,
  hasOtherOption,
  isJobEntryClient,
}) => {
  const renderContent = () => {
    if (currentStep === 4) {
      return (
        <Confirm
          questions={questions}
          clientNameValue={client?.clientName}
          brandNameValue={client?.brandName}
          workTitleValue={client?.workTitle}
          shopNameValue={clientShop?.shopName}
          businessTypeValue={businessTypeValue}
          occupationValue={occupationValue}
          employmentValue={employmentValue}
          employmentStatusValue={Number(employmentStatusValue) ?? undefined}
          continuedServiceYearsValue={continuedServiceYearsValue}
          checkboxValues={checkboxValues}
          radioValue={radioValues}
          textAreaValue={textAreaValue}
          satisfactionRateValue={satisfactionRateValue}
          termsPath={termsPath}
          guidelinePath={guidelinePath}
          handleClickPrevStepButton={handleClickPrevStepButton}
          handleClickNextStepButton={handleClickNextStepButton}
          handleClickSubmitButton={handleClickSubmitButton}
          salaryArray={salaryArray}
          bonusArray={bonusArray}
          hasOtherOption={hasOtherOption}
          isJobEntryClient={isJobEntryClient}
        />
      );
    }
    return (
      <div className={styles.wrapper}>
        <DotStepIndicator
          currentStep={currentStep}
          textArray={[
            "投稿先を選んでください",
            "投稿先の実態を教えてください",
            "求人情報の改善にご協力ください",
          ]}
        />
        {currentStep === 1 && (
          <>
            {/* 基本情報（投稿先企業） 就業情報（就業状況・勤続年数・職種・雇用形態） */}
            <Information />
            <ClientSelectSection
              title="投稿先を選ぶ"
              subtitle="投稿先"
              isShowAnnotation
              // 投稿先企業選択
              searchResults={searchResults}
              isShowSearchModal={isShowSearchModal}
              displaySearchText={displaySearchText}
              isSearching={isSearching}
              userContactPath={userContactPath}
              jobEntryButtons={jobEntryButtons}
              isShowClientsDetail={isShowClientsDetail}
              checkedJobEntryId={checkedJobEntryId}
              // values
              client={client ?? undefined}
              clientShop={clientShop ?? undefined}
              clientNameValue={clientNameValue}
              shopNameValue={shopNameValue}
              // error texts
              clientErrorText={errorTexts.client || errorTexts.clientShop}
              clientNameErrorText={errorTexts.clientName}
              shopNameErrorText={errorTexts.shopName}
              tooManyResultErrorText={tooManyResultErrorText}
              noResultError={noResultError}
              // handlers
              handleShowModal={handleShowModal}
              handleCloseModal={handleCloseModal}
              handleClientSearch={handleClientSearch}
              handleClientSelect={handleClientSelect}
              handleChangeClientNameValue={handleChangeClientNameValue}
              handleChangeShopNameValue={handleChangeShopNameValue}
              handleShowTextButton={handleShowTextButton}
              handleReturnToMyPageTop={handleReturnToMyPageTop}
              // refs
              clientRef={inputContainerRefs?.current.client}
            />
            <hr className={styles.hr} />
            <div className={styles.giftDescWrapper}>
              <GuidanceBox themeColor="gray">
                <p className={styles.giftDescTitle}>カフェギフトについて</p>
                <ul className={styles.giftDescUnorderedList}>
                  <li className={styles.giftDescList}>
                    ご登録のメールアドレスへ送付します（以下目安）
                    <br />
                    回答が毎月25日まで：翌月末
                    <br />
                    回答が毎月26日以降：翌々月末
                  </li>
                  <li className={styles.giftDescList}>
                    公開に適さない回答は付与の対象外です。付与の対象外となる場合のご連絡はいたしませんので、ご了承ください。
                  </li>
                </ul>
              </GuidanceBox>
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonOuter}>
                <Button theme="primary" onClick={handleClickNextStepButton}>
                  投稿先を保存してスタート
                </Button>
              </div>
            </div>
          </>
        )}
        {currentStep === 2 && (
          <>
            <div className={styles.img}>
              <LazyLoadImage
                width={143}
                height={56}
                src="/images/user_frontend/global/img_conversation.png"
                alt="話し合っている様子の画像"
              />
            </div>
            <SelectedClientInfo
              clientName={client?.clientName}
              brandName={client?.brandName}
              shopName={clientShop?.shopName}
              workTitle={client?.workTitle}
            />
            <div className={styles.guidanceWrapper}>
              <GuidanceBox themeColor="pink">
                <div className={styles.flex}>
                  <Icon name="close-view" fontSize="16px" color={colors.Biyo} />
                  <p>
                    企業への口コミは、リジョブおよび関連サイトに<b>匿名</b>で掲載されます。
                  </p>
                </div>
                <div className={styles.flex}>
                  <Icon name="attention" fontSize="16px" color={colors.Biyo} />
                  <p>
                    <b>個人情報やプライバシーへの配慮</b>をお願いします。
                  </p>
                </div>
              </GuidanceBox>
            </div>
            <Employment
              // 就業状況の質問項目（ラジオボタン・セレクトボックス）
              questions={questions.employmentQuestions}
              // values
              employmentStatusValue={employmentStatusValue ?? undefined}
              continuedServiceYearsValue={continuedServiceYearsValue ?? undefined}
              businessTypeValue={businessTypeValue ?? undefined}
              occupationValue={occupationValue ?? undefined}
              employmentValue={employmentValue ?? undefined}
              // error texts
              employmentStatusErrorText={errorTexts.employmentStatus}
              continuedServiceYearsErrorText={errorTexts.continuedServiceYears}
              businessTypesErrorText={errorTexts.businessType}
              occupationsErrorText={errorTexts.occupation}
              employmentsErrorText={errorTexts.employment}
              // handlers
              handleBusinessTypeSelect={handleBusinessTypeSelect}
              handleEmploymentStatusChange={handleEmploymentStatusChange}
              handleContinuedServiceYearSelect={handleContinuedServiceYearSelect}
              handleOccupationSelect={handleOccupationSelect}
              handleEmploymentSelect={handleEmploymentSelect}
              // refs
              businessTypeIdRef={inputContainerRefs?.current?.businessTypeId}
              employmentStatusRef={inputContainerRefs?.current?.employmentStatus}
              continuedServiceYearsRef={inputContainerRefs?.current.continuedServiceYears}
              occupationIdRef={inputContainerRefs?.current?.occupationId}
              employmentIdRef={inputContainerRefs?.current?.employmentId}
            />
            {/* 待遇面の口コミ 社風面の口コミ */}
            <TreatmentAndCulture
              // 質問項目（チェックボックス）
              questions={questions.treatmentAndCultureQuestions}
              // values
              checkboxValues={checkboxValues}
              // error texts
              treatmentsErrorText={errorTexts.treatments}
              culturesErrorText={errorTexts.cultures}
              // handlers
              handleCheckboxChange={handleCheckboxChange}
              isDisabled={false} // TODO: fixme
              treatmentsRef={inputContainerRefs?.current?.treatments}
              culturesRef={inputContainerRefs?.current?.cultures}
            />
            {/* 職場の口コミ（特徴） */}
            <WorkplaceReviewFeature
              // 質問項目（ラジオボタン）
              questions={questions.workplaceReviewFeatureQuestions}
              // values
              radioValues={radioValues}
              // error texts
              errorTexts={errorTexts}
              // handlers
              handleRadioChange={handleRadioChange}
              atmosphereRef={inputContainerRefs?.current?.atmosphere}
              salaryStandardRef={inputContainerRefs?.current?.salaryStandard}
              debutRef={inputContainerRefs?.current?.debut}
              educationRef={inputContainerRefs?.current?.education}
              workTimeRef={inputContainerRefs?.current?.workTime}
              salaryChangeRef={inputContainerRefs?.current?.salaryChange}
            />
            {/* 給与データ */}
            {typeof employmentValue !== "undefined" && employmentValue !== EMPLOYMENT.OTHER_ID && (
              <SalaryData
                // values
                monthlyIncomeValue={monthlyIncomeValue}
                minimumGuaranteedIncomeValue={minimumGuaranteedIncomeValue}
                grossMonthlyIncomeValue={grossMonthlyIncomeValue}
                hourlyWageValue={hourlyWageValue}
                noGuaranteedIncomeFlag={noGuaranteedIncomeFlag}
                noBonusFlag={noBonusFlag}
                hiddenFlag={hiddenFlag}
                employmentValue={employmentValue}
                // error texts
                monthlyIncomeErrorText={errorTexts.monthlyIncome}
                minimumGuaranteedIncomeErrorText={errorTexts.minimumGuaranteedIncome}
                grossMonthlyIncomeErrorText={errorTexts.grossMonthlyIncome}
                hourlyWageErrorText={errorTexts.hourlyWage}
                bonusInputsProps={{
                  ...bonusInputsProps,
                  errorBonusPaymentYearOptionsText: errorTexts.paymentYear,
                  errorBonusPaymentCountOptionsText: errorTexts.paymentCount,
                  errorBonusTotalAmountText: errorTexts.totalAmount,
                }}
                // handlers
                handleMonthlyIncomeChange={handleMonthlyIncomeChange}
                handleMinimumGuaranteedIncomeChange={handleMinimumGuaranteedIncomeChange}
                handleGrossMonthlyIncomeChange={handleGrossMonthlyIncomeChange}
                handleHourlyWageChange={handleHourlyWageChange}
                handleMinimumGuaranteedIncomeCheckboxChange={
                  handleMinimumGuaranteedIncomeCheckboxChange
                }
                handleBonusCheckboxChange={handleBonusCheckboxChange}
                handleHiddenFlagCheckboxChange={handleHiddenFlagCheckboxChange}
                // refs
                monthlyIncomeRef={inputContainerRefs?.current?.monthlyIncome}
                minimumGuaranteedIncomeRef={inputContainerRefs?.current?.minimumGuaranteedIncome}
                grossMonthlyIncomeRef={inputContainerRefs?.current?.grossMonthlyIncome}
                hourlyWageRef={inputContainerRefs?.current?.hourlyWage}
                bonusRef={inputContainerRefs?.current?.paymentYear}
              />
            )}
            {/* 職場の口コミ（あなたの考え/想い） */}
            <WorkplaceReviewThought
              // 質問項目（テキストエリア）
              questions={questions.workplaceReviewThoughtQuestions}
              // values
              textAreaValues={textAreaValue}
              // error texts
              errorTexts={errorTexts}
              // handlers
              handleTextAreaChange={handleTextAreaChange}
              workplaceMatchingRef={inputContainerRefs?.current?.workplaceMatching}
              interestRewardRef={inputContainerRefs?.current?.interestReward}
              // refs
              challengeRef={inputContainerRefs?.current?.challenge}
              expectationRef={inputContainerRefs?.current?.expectation}
            />
            <div className={styles.buttonContainer}>
              <div className={styles.halfButtonOuter}>
                <Button theme="tertiary" onClick={handleClickPrevStepButton}>
                  戻る
                </Button>
              </div>
              <div className={styles.halfButtonOuter}>
                <Button theme="primary" onClick={handleClickNextStepButton}>
                  次へ
                </Button>
              </div>
            </div>
          </>
        )}
        {currentStep === 3 && (
          <>
            <div className={styles.usingImg}>
              <LazyLoadImage
                width={70}
                height={81}
                src="/images/user_frontend/global/img_using_phone.png"
                alt="スマホで検索している様子の画像"
              />
            </div>
            {/* 応募の決め手 - リジョブ経由の求人応募の場合のみ表示 */}
            {isJobEntryClient && (
              <DecidingFactors
                // 質問項目
                questions={questions.decidingFactorsQuestions}
                // values
                decidingFactorsValues={checkboxValues.decidingFactors ?? []}
                // error texts
                decidingFactorsErrorText={errorTexts.decidingFactors}
                // handlers
                handleCheckboxChange={handleCheckboxChange}
                // refs
                decidingFactorsRef={inputContainerRefs?.current?.decidingFactors}
              />
            )}
            {/* リジョブへの満足度・感想 */}
            <Satisfaction
              // 質問項目(セレクトボックス)
              questions={questions.satisfactionAndThoughtsQuestions}
              // values
              satisfactionRateValue={satisfactionRateValue}
              chooseRejobReasonValues={checkboxValues.chooseRejobReasons ?? []}
              chooseRejobReasonDetailValue={textAreaValue.chooseRejobReasonDetail ?? ""}
              thoughtsValue={textAreaValue.thoughts ?? ""}
              // error texts
              satisfactionErrorText={errorTexts.satisfactionRate}
              chooseRejobReasonDetailErrorText={errorTexts.chooseRejobReasonDetail}
              thoughtsErrorText={errorTexts.thoughts}
              // handlers
              handleSatisfactionRateChange={handleSatisfactionRateChange}
              handleCheckboxChange={handleCheckboxChange}
              handleTextAreaChange={handleTextAreaChange}
              // refs
              satisfactionRateRef={inputContainerRefs?.current?.satisfactionRate}
              chooseRejobReasonDetailRef={inputContainerRefs?.current?.chooseRejobReasonDetail}
              thoughtsRef={inputContainerRefs?.current?.thoughts}
              // 理由カテゴリ名
              reasonCategoryName="chooseRejobReasons"
              enableChooseRejobReasonText={checkboxValues.chooseRejobReasons?.some((id) => {
                const otherOption =
                  questions.satisfactionAndThoughtsQuestions.reason &&
                  Array.isArray(questions.satisfactionAndThoughtsQuestions.reason)
                    ? questions.satisfactionAndThoughtsQuestions.reason.find((option) =>
                        option.optionText.includes("その他"),
                      )
                    : undefined;
                return otherOption && id === otherOption.optionId;
              })}
            />
            <div className={styles.buttonContainer}>
              <div className={styles.halfButtonOuter}>
                <Button theme="tertiary" onClick={handleClickPrevStepButton}>
                  戻る
                </Button>
              </div>
              <div className={styles.halfButtonOuter}>
                <Button theme="primary" onClick={handleClickNextStepButton}>
                  確認画面へ
                </Button>
              </div>
            </div>
          </>
        )}
        <RestoreClientReviewInputsModal
          isShowBackupModal={isShowBackupModal}
          restoreBackupPath={restoreBackupPath}
          deleteBackupPath={deleteBackupPath}
          handleRestore={handleRestore}
        />
      </div>
    );
  };

  return <>{renderContent()}</>;
};
